import { css } from '@emotion/react';
import { CardAffinity, CardType } from '@mythos/game/CardTypes';
import { AGES_PER_GAME, TURNS_PER_AGE } from '@mythos/game/GameConstants';
import {
  CardWithID,
  computePlayerFavorDetails,
  makeContextFromGame,
} from '@mythos/game/Rules';
import Stats, { augmentGameEvents, MilitaryStats } from '@mythos/game/Stats';
import { ageString } from '@mythos/game/Utility';
import * as d3 from 'd3';
import invariant from 'invariant';
import { forEach, map, mapValues, reduce, times } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import CardView, { BasicCardRenderer } from './CardView';
import { ColorPalette, UIColorPalette } from './ColorPalette';
import { UserWithColor } from './GraphQLHelpers';
import HoverCardStore from './HoverCardStore';
import { LobbyButtonStyles } from './LobbyButton';
import { PlayerFavorDisplay } from './PlayerSummaryView';
import { CardTypeToSymbol, SymbolView } from './Symbols';
import TributeCardView from './TributeCardView';
import { FinishPosition } from './ViewUtilities';

import { EventTypes } from '@mythos/game/GameEvents';
import {
  GameViewModel,
  resetGameViewModel,
  updateGameViewModel,
} from '@mythos/game/GameViewModel';
import { Phase } from '@mythos/game/Phases';
import '../assets/css/end-of-game.css';
import GameStateView from './GameStateView';
import GameTimeView from './GameTimeView';
import { GlobalAreaView } from './GameView';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';
import PlayerBoardView from './PlayerBoardView';
import { PieChart } from './StatsViews';
import UserHoverView from './UserHoverView';

function createLineChart(dom: HTMLDivElement, props: LineChartProps) {
  var data = props.data;

  //** Domain
  var xExtend = d3.extent(data[0].values, function (d) {
      return d.turn;
    }),
    yMin = d3.min(data, function (d) {
      return d3.min(d.values, function (v) {
        return v.value;
      });
    }),
    yMax = d3.max(data, function (d) {
      return d3.max(d.values, function (v) {
        return v.value;
      });
    });
  let tickLabels = data[0].values.map((val, i) => {
    if (i === 0) return 'Start';
    // 'End' not rendered for now
    if (i === props.turnsPerAge * AGES_PER_GAME + 1) return 'End';
    return `${((i - 1) % props.turnsPerAge) + 1}`;
  });
  //** Chart Dimension
  var margin = { top: 20, left: 50, right: 50, bottom: 20 },
    svgWidth = 710,
    svgHeight = 185,
    width = svgWidth - margin.left - margin.right,
    height = svgHeight - margin.top - margin.bottom,
    //** Scale
    xScale = d3.scaleLinear().range([0, width]),
    yScale = d3.scaleLinear().range([height, 0]),
    //** Axis
    xAxis = d3
      .axisBottom(xScale)
      .ticks(Math.max(1, data[0].values.length - 1))
      .tickFormat((d, i) => tickLabels[i]),
    yAxis = d3
      .axisLeft(yScale)
      .ticks(Math.min(props.turnsPerAge, yMax! - yMin! + 1)),
    //** Line function
    line = d3
      .line<{ turn: Number; value: number }>()
      .x(function (d) {
        return xScale(d.turn);
      })
      .y(function (d) {
        return yScale(d.value);
      });

  //** Color

  invariant(xExtend[0] !== undefined, 'ts');
  xScale.domain(xExtend);
  yScale.domain([yMin!, yMax!]);

  //** Setup container and skeleton of chart
  var svg = d3.select(dom).selectAll('svg').data([data]),
    svgEnter = svg
      .enter()
      .append('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .append('g')
      .attr('class', 'graph')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

  // Draw axis
  svgEnter.append('g').attr('class', 'axis x');

  svgEnter
    .select<SVGSVGElement>('g.axis.x')
    .attr('transform', 'translate(0, ' + height + ')')
    .call(xAxis);

  svgEnter.append('g').attr('class', 'axis y').call(yAxis);

  // Line

  const lines = svgEnter
    .append('g')
    .attr('class', 'line-container')
    .selectAll('.line')
    .data(data);

  lines
    .enter()
    .append('path')
    .attr('class', function (d, i) {
      return 'line' + d.id;
    })
    .attr('fill', 'none')
    .attr('stroke', function (d, i) {
      return d.color;
    })
    .attr('d', function (d) {
      return line(d.values);
    });

  // Hover line & invisible rect
  var hoverLineGroup = svgEnter.append('g').attr('class', 'hover-line');

  // Add the line to the group
  var hoverLine = hoverLineGroup
    .append('line')
    .attr('id', 'hover-line')
    .attr('x1', 0)
    .attr('x2', 0)
    .attr('y1', 0)
    .attr('y2', height)
    .style('stroke-opacity', 0);

  // Tooltip
  var toolTip = d3.select(dom).append('div').attr('class', 'chart-tooltip');
  var toolTipTurn = toolTip.append('text').attr('class', 'chart-tooltip-turn');
  var toolTipPlayerList = toolTip
    .append('ul')
    .attr('class', 'chart-tooltip-player-list');

  const playerText = data.map((playerData) => {
    return toolTipPlayerList
      .append('li')
      .attr('class', 'chart-tooltip-player-text')
      .style('color', playerData.color);
  });

  // Mouse action helpers
  const mouseOut = () => {
    // Hide Tooltip and Hover line
    toolTip.style('visibility', 'hidden');
    hoverLine.style('stroke-opacity', 0);
  };

  const mouseMove = (event: React.MouseEvent<SVGSVGElement>, d: any) => {
    var mouse = d3.pointer(event),
      mouseX = mouse[0],
      mouseY = mouse[1],
      turn = Math.round(xScale.invert(mouseX));

    var arr = data[0].values;
    arr.sort(function (a, b) {
      return a.turn - b.turn;
    });

    playerText.forEach((obj, i) => {
      obj.text(data[i].series + ' : ' + data[i].values[turn].value);
    });

    // Display Hover line
    hoverLine.attr('x1', mouseX).attr('x2', mouseX).style('stroke-opacity', 1);

    const getVirtualPosition = (el: Element) => {
      if (el.parentNode?.nodeName === 'svg') {
        return (el.parentNode as Element).getBoundingClientRect();
      }
      return getVirtualPosition(el.parentNode as Element);
    };
    var virtual_position = getVirtualPosition(event.target as Element);

    // Display tool tip
    toolTip
      .style('visibility', 'visible')
      .style('left', virtual_position.left + mouseX + 70 + 'px')
      .style('top', virtual_position.top + mouseY + 20 + 'px');
    toolTipTurn.text('Turn ' + turn);
  };

  // Create a invisible rect for mouse tracking
  svgEnter
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'none')
    .style('pointer-events', 'all')
    .on('mouseout', mouseOut)
    .on('mousemove', mouseMove);
}

interface LineChartProps {
  title: string;
  data: {
    series: any;
    values: { turn: number; value: number }[];
    id: string;
    color: string;
  }[];
  turnsPerAge: number;
}

const LineChart: React.FC<LineChartProps> = ({
  title = '',
  data,
  turnsPerAge,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      createLineChart(chartRef.current, { title, data, turnsPerAge });
    }
  }, [title, data, turnsPerAge]);

  return (
    <div ref={chartRef}>
      <h4> {title} </h4>
    </div>
  );
};

interface PlayerTurnTableProps<TDatum> {
  title: string;
  data: { [k: string]: TDatum }[];
  userByID: Map<string, UserWithColor>;
  turnsPerAge: number;
  datumToContentFunc: (datum: TDatum) => any;
  includeTurn0?: boolean;
  datumToCellClassFunc?: (datum: TDatum) => string;
  datumToCellStyleFunc?: (datum: TDatum) => React.CSSProperties;
  onEnterDatum?: (datum: TDatum) => void;
  onLeaveDatum?: (datum: TDatum) => void;
  className?: string;
}

const PlayerTurnTable = <TDatum = any,>({
  data,
  userByID,
  datumToContentFunc,
  datumToCellClassFunc,
  datumToCellStyleFunc,
  onEnterDatum,
  onLeaveDatum,
  title,
  turnsPerAge,
  includeTurn0 = false,
  className = '',
}: PlayerTurnTableProps<TDatum>) => {
  const turns = data.length;

  var player_data: { [k: string]: TDatum[] } = {};
  data.forEach((entry) => {
    forEach(entry, (result, player) => {
      if (!player_data[player]) {
        player_data[player] = [];
      }
      player_data[player].push(result);
    });
  });

  var columns = times(turns, (i) => {
    return <col key={i + 1} />;
  });

  var xaxis = times(turns, (turn) => {
    let turn_in_age = 0;
    if (includeTurn0) {
      if (turn > 0) {
        turn_in_age = ((turn - 1) % turnsPerAge) + 1;
      }
    } else {
      turn_in_age = (turn % turnsPerAge) + 1;
    }
    return (
      <th className={'turn_' + turn_in_age} key={turn}>
        {turn_in_age}
      </th>
    );
  });
  xaxis = [<th key="header">Player</th>].concat(xaxis);

  var table_contents = [<tr key="xaxis">{xaxis}</tr>];

  table_contents = table_contents.concat(
    map(player_data, (entries, playerID) => {
      const user = userByID.get(playerID)!;
      var name_style = { color: user.color };
      var row = [
        <td key="name" style={name_style}>
          {user.name}
        </td>,
      ];

      row = row.concat(
        entries.map((datum, i) => {
          const content = datumToContentFunc(datum);
          const class_name = datumToCellClassFunc?.(datum);
          const style = datumToCellStyleFunc?.(datum);

          return (
            <td
              key={i}
              className={class_name}
              style={style}
              onMouseEnter={onEnterDatum && (() => onEnterDatum(datum))}
              onMouseLeave={onLeaveDatum && (() => onLeaveDatum(datum))}
            >
              {content}
            </td>
          );
        }),
      );

      return <tr key={playerID}>{row}</tr>;
    }),
  );

  let tableClassName =
    'player-turn-table ' +
    className +
    (includeTurn0 ? ' stats-table-include-turn0' : '');
  return (
    <div>
      <h4 className="graphs-title">{title}</h4>
      <table className={tableClassName}>
        <colgroup>{columns}</colgroup>
        <tbody>{table_contents}</tbody>
      </table>
    </div>
  );
};

type PlayerLineGraphProps = {
  userByID: Map<string, UserWithColor>;
  data: any[];
  turnsPerAge: number;
  title: string;
};
const PlayerLineGraph: React.FC<PlayerLineGraphProps> = (props) => {
  let { userByID, data, title } = props;
  let series = Array.from(userByID.entries()).map(([id, user]) => {
    let values = data.map((data_by_player, i) => {
      let value = data_by_player[id];
      let turn = i;
      return { turn, value };
    });
    return { series: user.name, values, id, color: user.color };
  });

  return (
    <div className="graphs-favorgained-line">
      <LineChart
        key={title}
        data={series}
        title={title}
        turnsPerAge={props.turnsPerAge}
      />
    </div>
  );
};

const LineChartTabContainer = (props: {
  children: React.ReactElement<PlayerLineGraphProps>[];
}) => {
  const { children } = props;
  const [selectedTab, setSelectedTab] = React.useState(0);
  return (
    <div className="line-chart-tab-container">
      <div className="line-chart-tab-buttons">
        {React.Children.map(children, (chart, i) => (
          <button
            key={chart.key || undefined}
            onClick={() => setSelectedTab(i)}
            className={selectedTab === i ? 'selected' : ''}
          >
            {chart.props.title}
          </button>
        ))}
      </div>
      <div className="line-chart-tab-content">{children[selectedTab]}</div>
    </div>
  );
};

const CARD_AFFINITY_TO_COLOR: Record<CardAffinity, string> = {
  [CardAffinity.Basic]: UIColorPalette.endGameCardBasic,
  [CardAffinity.Derion]: UIColorPalette.endGameCardDerion,
  [CardAffinity.Tyros]: UIColorPalette.endGameCardTyros,
  [CardAffinity.Zeona]: UIColorPalette.endGameCardZeona,
  [CardAffinity.Leader]: UIColorPalette.endGameTribute0,
  [CardAffinity.None]: UIColorPalette.endGameTribute0,
};
// TODO: use Palette colors

const CARD_TYPE_TO_COLOR: Record<CardType, string> = {
  [CardType.Building]: 'rgb(198, 198, 198)',
  [CardType.Unit]: 'rgb(176, 126, 74)',
  [CardType.Event]: 'rgb(234, 163, 0)',
};
const TRIBUTE_COUNT_TO_COLOR: Record<0 | 1 | 2 | 3, string> = {
  0: UIColorPalette.endGameTribute0,
  1: UIColorPalette.endGameTribute1,
  2: UIColorPalette.endGameTribute2,
  3: UIColorPalette.endGameTribute3,
};

function create_player_time_pie_chart(
  user_by_id: Map<string, UserWithColor>,
  title: string,
  count_by_id: { [k: string]: number },
) {
  let colors: string[] = [];
  let data = Array.from(user_by_id.entries()).map(([id, user]) => {
    colors.push(user.color);
    return { name: user.name, count: Math.floor(count_by_id[id] / 1000) };
  });
  return <PieChart data={data} colors={colors} title={title} />;
}

export default function EndOfGameView(props: {
  game: GameViewModel;
  userByID: Map<string, UserWithColor>;
}) {
  const { game, userByID } = props;

  const [minimize, setMinimize] = React.useState(false);

  const onClickMinimize = () => {
    setMinimize(!minimize);
  };

  const timelineTicks = useMemo(() => {
    return computeTimelineTicks(game);
  }, [game]);

  const players = game.playerStates;
  if (game.gameEndTimestamp === null) {
    return null;
  }
  let affinitiesCharts: React.ReactElement[] = [];
  let typesCharts: React.ReactElement[] = [];

  game.playerStates.forEach((player) => {
    const affinityCounts = Object.fromEntries(
      Object.values(CardAffinity).map((affinity) => [affinity, 0]),
    ) as Record<CardAffinity, number>;
    const typeCounts = Object.fromEntries(
      Object.values(CardType).map((type) => [type, 0]),
    ) as Record<CardType, number>;

    player.cards.forEach((card) => {
      affinityCounts[card.affinity]++;
      typeCounts[card.type]++;
    });

    const affinityData = Object.entries(affinityCounts).map(
      ([affinity, count]) => ({
        name: affinity,
        count,
      }),
    );
    const affinityColors = Object.keys(affinityCounts).map((affinity) => {
      return CARD_AFFINITY_TO_COLOR[affinity as CardAffinity];
    });
    const typeData = Object.entries(typeCounts).map(([type, count]) => ({
      name: type,
      count,
    }));
    const typeColors = Object.keys(typeCounts).map((type) => {
      return CARD_TYPE_TO_COLOR[type as CardType];
    });

    const user = userByID.get(player.userID)!;
    affinitiesCharts.push(
      <PieChart
        key={`affinity-chart-${player.userID}`}
        data={affinityData}
        colors={affinityColors}
        title={user.name}
        titleColor={user.color}
      />,
    );
    typesCharts.push(
      <PieChart
        key={`type-chart-${player.userID}`}
        data={typeData}
        colors={typeColors}
        title={user.name}
        titleColor={user.color}
      />,
    );
  });

  const extract_field = <R, T = any>(
    raw_data: any,
    f: (data: T) => R,
  ): { [k: string]: R }[] => {
    return raw_data.map((data_by_player: any) => {
      return mapValues(data_by_player, f) as { [k: string]: R };
    });
  };
  const augmented_events = augmentGameEvents(game);

  let raw_favor_data = Stats.computeGainedFavorStats(game, augmented_events);
  let tribute_favor_data = extract_field(raw_favor_data, (data) => {
    return data.total_tribute_favor;
  });
  let card_favor_data = extract_field(raw_favor_data, (data) => {
    return data.total_card_favor;
  });
  let trigger_favor_data = extract_field(raw_favor_data, (data) => {
    return data.total_triggered_favor;
  });
  let prophecy_favor_data = extract_field(raw_favor_data, (data) => {
    return data.total_prophecy_favor;
  });
  let total_favor_data = extract_field(raw_favor_data, (data) => {
    return (
      data.total_tribute_favor +
      data.total_card_favor +
      data.total_triggered_favor +
      data.total_prophecy_favor
    );
  });

  const favor_piecharts = game.playerStates.map((player) => {
    const favor_data: { name: string; count: number }[] = [
      {
        name: 'Tributes',
        count: tribute_favor_data[AGES_PER_GAME * TURNS_PER_AGE][player.userID],
      },
      {
        name: 'Cards',
        count: card_favor_data[AGES_PER_GAME * TURNS_PER_AGE][player.userID],
      },
      {
        name: 'Prophecies',
        count:
          prophecy_favor_data[AGES_PER_GAME * TURNS_PER_AGE][player.userID],
      },
      {
        name: 'Triggers',
        count: trigger_favor_data[AGES_PER_GAME * TURNS_PER_AGE][player.userID],
      },
    ];
    const other_favor =
      total_favor_data[AGES_PER_GAME * TURNS_PER_AGE][player.userID] -
      favor_data.reduce((sum, { count }) => sum + count, 0);
    favor_data.push({ name: 'Other', count: other_favor });

    // XXX: maybe better color selection/decouple from tribute count
    var colors: string[] = [
      TRIBUTE_COUNT_TO_COLOR[3],
      TRIBUTE_COUNT_TO_COLOR[2],
      TRIBUTE_COUNT_TO_COLOR[1],
      TRIBUTE_COUNT_TO_COLOR[0],
      TRIBUTE_COUNT_TO_COLOR[0],
    ];

    const user = userByID.get(player.userID)!;
    return (
      <div key={player.userID} className="graph-cardtypes-pie">
        <PieChart
          data={favor_data}
          colors={colors}
          title={user.name}
          titleColor={user.color}
        />
      </div>
    );
  });

  let favor_data_charts = [
    <PlayerLineGraph
      key="tribute_favor_data"
      data={tribute_favor_data}
      userByID={userByID}
      title={'Favor from Tributes'}
      turnsPerAge={game.turnsPerAge}
    />,
    <PlayerLineGraph
      key="card_favor_data"
      data={card_favor_data}
      userByID={userByID}
      title={'Base Favor from Cards'}
      turnsPerAge={game.turnsPerAge}
    />,
    <PlayerLineGraph
      key="prophecy_favor_data"
      data={prophecy_favor_data}
      userByID={userByID}
      title={'Favor from Prophecies'}
      turnsPerAge={game.turnsPerAge}
    />,
  ];
  // only use trigger favor data if there is any
  if (
    trigger_favor_data
      .reduce(
        (data, turn_data) => data.concat(Object.values(turn_data) as any),
        [] as any[],
      )
      .some((favor_value: any) => !!favor_value)
  ) {
    favor_data_charts.push(
      <PlayerLineGraph
        data={trigger_favor_data}
        userByID={userByID}
        title={'Favor from Triggers'}
        turnsPerAge={game.turnsPerAge}
      />,
    );
  }

  const bid_data = Stats.computeBidData(game, augmented_events);
  let production_data = Stats.computeProductionData(game, augmented_events);
  let production_gold = extract_field(production_data, (data) => {
    return data.gold;
  });
  let production_military = extract_field(production_data, (data) => {
    return data.military;
  });
  let gold_income = extract_field(production_data, (data) => {
    return data.gold_income;
  });
  let military_income = extract_field(production_data, (data) => {
    return data.military_income;
  });
  let gold_base = extract_field(production_data, (data) => {
    return data.gold_base;
  });
  let military_base = extract_field(production_data, (data) => {
    return data.military_base;
  });
  let max_favor = Math.max(...players.map((player) => player.counters.favor));
  let winning_players = players.filter(
    (player) => max_favor === player.counters.favor,
  );
  let winning_players_content = winning_players.map((player, i) => {
    const user = userByID.get(player.userID)!;
    let style = {
      color: user.color,
      display: 'inline-block',
    };
    let name = user.name;
    let conjuction =
      i !== winning_players.length - 1 ? <span> and </span> : null;
    return (
      <span key={player.userID}>
        <span style={style}>{name}</span>
        {conjuction}
      </span>
    );
  });

  let sum_phase_time_data = (timing_data: any[]) => {
    return timing_data.map((turn: any) => {
      return reduce(
        turn,
        (memo: { [k: string]: number }, phase: any) => {
          forEach(phase, (time: number, user_id: string) => {
            memo[user_id] = (memo[user_id] || 0) + time;
          });
          return memo;
        },
        {} as { [k: string]: number },
      );
    });
  };

  let time_data = Stats.getTimingDataByTurn(game, augmented_events);
  let total_turn_time_data = sum_phase_time_data(time_data);
  let time_per_turn_data = extract_field(total_turn_time_data, (data) => {
    return Math.round(data / 1000);
  });
  let blame_time_data = Stats.computeBlameTimingDataByTurn(
    game,
    augmented_events,
  );
  let total_turn_blame_time_data = sum_phase_time_data(blame_time_data);
  let blame_time_per_turn_data = extract_field(
    total_turn_blame_time_data,
    (data) => {
      return Math.round(data / 1000);
    },
  );
  let solo_time_data = Stats.computeSoloThinkingTimingDataByTurn(
    game,
    augmented_events,
  );
  let total_turn_solo_time_data = sum_phase_time_data(solo_time_data);
  let solo_time_per_turn_data = extract_field(
    total_turn_solo_time_data,
    (data) => {
      return Math.round(data / 1000);
    },
  );

  function cumsum_turn_data(
    turn_data: {
      [k: string]: number;
    }[],
  ): {
    [k: string]: number;
  }[] {
    return turn_data.reduce(
      (acc, turn) => {
        const previous_turn = acc[acc.length - 1];

        const cum_turn = mapValues(turn, (value, key) => {
          return previous_turn[key] + value;
        }) as { [k: string]: number };

        return acc.concat(cum_turn);
      },
      [turn_data[0]],
    );
  }

  const time_per_turn_data_cumsum = cumsum_turn_data(time_per_turn_data);
  const blame_time_per_turn_data_cumsum = cumsum_turn_data(
    blame_time_per_turn_data,
  );
  const solo_time_per_turn_data_cumsum = cumsum_turn_data(
    solo_time_per_turn_data,
  );

  const militaryData = Stats.computeMilitaryStats(game, augmented_events);
  const cardStatsData = Stats.computeGainedCard(game, augmented_events);
  const tributeStatsData = Stats.computeGainedTributes(game, augmented_events);
  const conflictResultsRenderer = makeConflictResultsHoverRenderer({
    userByID,
    cardsByID: game.cardsByID as any,
  });
  return (
    <div css={EndOfGameViewStyles.container}>
      <div css={EndOfGameViewStyles.gameStateSection}>
        Game Over
        <GameTimeView
          extraSplitTime={game.gameEndTimestamp - game.gameStartTimestamp}
        />
      </div>
      <div css={EndOfGameViewStyles.titleSection}>
        <div css={EndOfGameViewStyles.titleTopSection}>
          <div css={EndOfGameViewStyles.winnersSection}>
            <div>Game over! {winning_players_content} Wins!</div>
            <EndGameLeaderboard game={game} userByID={userByID} />
          </div>
          <div css={EndOfGameViewStyles.returnSection}>
            <Link
              css={[LobbyButtonStyles.button, EndOfGameViewStyles.returnButton]}
              to={'/'}
            >
              Return to Lobby
            </Link>
          </div>
        </div>
        <div css={EndOfGameViewStyles.titleBottomSection}>
          <span> End of Game Stats </span>
          <button
            css={EndOfGameViewStyles.minimizeButton}
            onClick={onClickMinimize}
          >
            {' '}
            -{' '}
          </button>
        </div>
      </div>
      <div
        css={EndOfGameViewStyles.content}
        style={{ display: minimize ? 'none' : 'block' }}
      >
        <PlayerLineGraph
          data={total_favor_data}
          userByID={userByID}
          title={'Total Favor'}
          turnsPerAge={game.turnsPerAge}
        />
        <PlayerTurnTable
          title="Conflict Results"
          className="montecarlo-table"
          data={militaryData}
          userByID={userByID}
          turnsPerAge={game.turnsPerAge}
          datumToContentFunc={(result) => {
            return result?.text || '';
          }}
          datumToCellClassFunc={(result) => {
            return result
              ? result.text.replace('+', '_plus').replace('-', '_minus')
              : '';
          }}
          onEnterDatum={(datum) => {
            if (!datum) {
              return;
            }
            HoverCardStore.setPosition('bottom-right');
            HoverCardStore.setCard(datum, conflictResultsRenderer);
          }}
          onLeaveDatum={() => {
            HoverCardStore.setCard(null, null);
          }}
        />
        <h4 className="graphs-title">Card Affinity Distribution</h4>
        <div className="graphs-cardtypes-pie">{affinitiesCharts}</div>
        <h4 className="graphs-title">Card Type Distribution</h4>
        <div className="graphs-cardtypes-pie">{typesCharts}</div>
        <PlayerTurnTable
          title="Card gained by turn"
          className="card-stats-table"
          data={cardStatsData}
          userByID={userByID}
          includeTurn0={true}
          turnsPerAge={game.turnsPerAge}
          datumToContentFunc={(result) => {
            return (
              <SymbolView
                symbol={CardTypeToSymbol[result.type]!}
                style={{
                  height: 12,
                  width: 12,
                  filter: 'drop-shadow(0 0 2px #0a0a0d)',
                }}
              />
            );
          }}
          datumToCellStyleFunc={(result) => {
            return {
              backgroundColor: CARD_AFFINITY_TO_COLOR[result.affinity],
            };
          }}
          onEnterDatum={(datum) => {
            HoverCardStore.setPosition('bottom-right');
            HoverCardStore.setCard(datum, BasicCardRenderer);
          }}
          onLeaveDatum={() => {
            HoverCardStore.setCard(null, null);
          }}
        />
        <PlayerTurnTable
          title="Tributes gained by turn"
          className="tribute-stats-table"
          data={tributeStatsData}
          userByID={userByID}
          includeTurn0={false}
          turnsPerAge={game.turnsPerAge}
          datumToContentFunc={(result) => {
            return <>{result.length}</>;
          }}
          datumToCellStyleFunc={(result) => {
            return {
              backgroundColor:
                TRIBUTE_COUNT_TO_COLOR[result.length as 0 | 1 | 2 | 3],
            };
          }}
          onEnterDatum={(datum) => {
            HoverCardStore.setCard(datum, (props) => {
              const tributeIDs = props.card as string[];
              return (
                <div>
                  {tributeIDs.map((id) => {
                    const tribute = game.tributeCardsByID[id];
                    return (
                      <div style={{ zoom: 0.5 }}>
                        <TributeCardView card={tribute} />
                      </div>
                    );
                  })}
                </div>
              );
            });
            HoverCardStore.setPosition('top-right-offset');
          }}
          onLeaveDatum={() => {
            HoverCardStore.setCard(null, null);
          }}
        />
        <h4 className="graphs-title">Favor Distribution</h4>
        <div className="graphs-cardtypes-pie">{favor_piecharts}</div>
        <LineChartTabContainer>{favor_data_charts}</LineChartTabContainer>
        <LineChartTabContainer>
          <PlayerLineGraph
            data={bid_data}
            userByID={userByID}
            title={'military spent'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={production_military}
            userByID={userByID}
            title={'military tokens + base'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={military_income}
            userByID={userByID}
            title={'military generated'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={military_base}
            userByID={userByID}
            title={'base military'}
            turnsPerAge={game.turnsPerAge}
          />
        </LineChartTabContainer>
        <LineChartTabContainer>
          <PlayerLineGraph
            data={production_gold}
            userByID={userByID}
            title={'gold tokens + base'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={gold_income}
            userByID={userByID}
            title={'gold generated'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={gold_base}
            userByID={userByID}
            title={'base gold'}
            turnsPerAge={game.turnsPerAge}
          />
        </LineChartTabContainer>
        <LineChartTabContainer>
          <PlayerLineGraph
            data={time_per_turn_data_cumsum}
            userByID={userByID}
            title={'Time Spent'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={blame_time_per_turn_data_cumsum}
            userByID={userByID}
            title={'Blame Time'}
            turnsPerAge={game.turnsPerAge}
          />
          <PlayerLineGraph
            data={solo_time_per_turn_data_cumsum}
            userByID={userByID}
            title={'Solo Thinking Time'}
            turnsPerAge={game.turnsPerAge}
          />
        </LineChartTabContainer>
        <div className="graphs-cardtypes-pie">
          {create_player_time_pie_chart(
            userByID,
            'Total Time',
            Stats.computeTotalTimeByPlayer(game, augmented_events),
          )}
          {create_player_time_pie_chart(
            userByID,
            'Blame Time',
            Stats.computeTotalBlameTimeByPlayer(game, augmented_events),
          )}
          {create_player_time_pie_chart(
            userByID,
            'Solo Time',
            Stats.computeTotalSoloThinkingTimeByPlayer(game, augmented_events),
          )}
        </div>
        <div>
          <span className="disclaimer"> More graphs to come! </span>
        </div>
      </div>
      {false && (
        <EndOfGameReplayView
          userByID={userByID}
          timelineTicks={timelineTicks}
        />
      )}
    </div>
  );
}

const EndOfGameViewStyles = {
  container: css({
    margin: 5,
    marginBottom: 0,
    backgroundColor: UIColorPalette.logBG,
  }),
  content: css({
    padding: 15,
    overflowY: 'hidden',
    overflowX: 'hidden',
    backgroundColor: UIColorPalette.logBG,
    width: 751,
    margin: 'auto',
  }),
  gameStateSection: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 5,
    paddingLeft: 15,
    paddingRight: 15,
    marginBottom: 5,
    backgroundColor: 'rgb(55, 55, 55)',
    height: 25,
    color: 'rgb(235, 235, 235)',
  }),
  minimizeButton: css({
    position: 'absolute',
    right: 7,
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  returnSection: css({
    margin: 15,
  }),
  winnersSection: css({
    margin: 5,
  }),
  returnButton: css({
    textDecoration: 'none',
    color: 'black',
    fontSize: 16,
    paddingTop: 5,
    paddingBottom: 5,
  }),
  titleSection: css({
    display: 'block',
    color: 'rgb(235, 235, 235)',
    fontSize: 20,
    backgroundColor: 'rgb(55, 55, 55)',
    textAlign: 'center',
  }),
  titleTopSection: css({
    padding: 7,
    borderBottom: '1px solid rgb(85, 85, 85)',
  }),
  titleBottomSection: css({
    padding: 7,
    position: 'relative',
  }),
  disclaimer: css({
    textAlign: 'center',
    margin: 10,
    fontSize: 15,
    width: '100%',
    display: 'block',
  }),
  timelineSection: css({}),
  playerBoardsContainer: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 5,
  }),
};

function EndOfGameReplayView(props: {
  userByID: Map<string, UserWithColor>;
  timelineTicks: TimelineTick[];
}) {
  const { userByID, timelineTicks } = props;
  const [timelineTickIndex, setTimelineTickIndex] = React.useState(
    timelineTicks.length - 1,
  );

  const game = timelineTicks[timelineTickIndex].viewModel;

  return (
    <div css={EndOfGameViewStyles.timelineSection}>
      <input
        type="range"
        min={0}
        max={timelineTicks.length - 1}
        value={timelineTickIndex}
        onChange={(e) => {
          setTimelineTickIndex(Number(e.target.value));
        }}
      />
      <GameStateView game={game} />
      <GlobalAreaView game={game} userByID={userByID} selfPlayerView={null} />
      <div css={EndOfGameViewStyles.playerBoardsContainer}>
        {game.playerStates.map((player) => (
          <PlayerBoardView
            key={player.userID}
            player={player}
            game={game}
            user={userByID.get(player.userID)!}
          />
        ))}
      </div>
    </div>
  );
}

function makeConflictResultsHoverRenderer(data: {
  userByID: Map<string, UserWithColor>;
  cardsByID: { [k: string]: CardWithID };
}) {
  const { cardsByID, userByID } = data;
  return (props: { card: MilitaryStats }) => {
    const stats = props.card;
    return (
      <div>
        <CardView card={cardsByID[stats.cardID]} />
        <div css={ConflictResultsStyle.container}>
          Conflict Results - Age {ageString(stats.age)} Turn {stats.turn}
          {Object.keys(stats.playerIDToBonus).map((playerID) => {
            const bonus = stats.playerIDToBonus[playerID];
            const probability = stats.playerIDToProbability[playerID];
            return (
              <div key={playerID}>
                <span
                  css={ConflictResultsStyle.player}
                  style={{
                    color:
                      playerID === stats.winningPlayerID
                        ? UIColorPalette.eogConflictResultsWinnerName
                        : UIColorPalette.eogConflictResultsPlayerName,
                  }}
                >
                  {userByID.get(playerID)!.name}
                </span>{' '}
                +{bonus} ({Math.round(probability * 100)}%)
              </div>
            );
          })}
        </div>
      </div>
    );
  };
}

const ConflictResultsStyle = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: UIColorPalette.backgroundWhite,
    zoom: 1 / 2.5,
    padding: 5,
    color: UIColorPalette.eogConflictResultsTitleText,
  }),
  player: css({
    fontWeight: 'bold',
  }),
};

type TimelineTick = {
  turn: number;
  age: number;
  eventCount: number;
  viewModel: GameViewModel;
} & (
  | {
      type: 'Phase';
      phase: Phase;
    }
  | {
      type: 'End';
    }
);
function computeTimelineTicks(game: GameViewModel): TimelineTick[] {
  let currentGameViewModel = resetGameViewModel(game);

  // const initalGameViewModel =
  // let gameState =
  const ticks: TimelineTick[] = [];
  let currentTurn = 0;
  let currentAge = 0;
  game.events.forEach((event, i) => {
    currentGameViewModel = updateGameViewModel(currentGameViewModel, [event]);

    if (event.type === EventTypes.SET_UP_AGE) {
      currentAge = event.payload.age;
      currentTurn = 0;
    } else if (event.type === EventTypes.SET_UP_TURN) {
      currentTurn = event.payload.turn;
    } else if (event.type === EventTypes.SET_UP_PHASE) {
      if (
        event.payload.phase === Phase.PLANNING ||
        event.payload.phase === Phase.RESOLUTION
      ) {
        ticks.push({
          turn: currentTurn,
          age: currentAge,
          eventCount: i + 1,
          viewModel: currentGameViewModel,
          type: 'Phase',
          phase: event.payload.phase,
        });
      }
    } else if (event.type === EventTypes.END_OF_GAME) {
      ticks.push({
        turn: currentTurn,
        age: currentAge,
        eventCount: i + 1,
        viewModel: currentGameViewModel,
        type: 'End',
      });
    }
  });
  return ticks;
}
export function EndGameLeaderboard(props: {
  game: GameViewModel;
  userByID: Map<string, UserWithColor>;
}) {
  const { game, userByID } = props;
  const players = [...game.playerStates].sort(
    (a, b) => b.counters.favor - a.counters.favor,
  );
  let tieData = {
    lastScore: Number.NEGATIVE_INFINITY,
    tiedRank: 0,
  };
  const playerAndRank = players.map((player, index) => {
    if (player.counters.favor === tieData.lastScore) {
      return { player, rank: tieData.tiedRank };
    } else {
      tieData.lastScore = player.counters.favor;
      tieData.tiedRank = index + 1;
    }
    return { player, rank: tieData.tiedRank };
  });

  const showRatings = game.playerInfos.every(
    (player) =>
      player.botType === null &&
      userByID.get(player.userID)?.stats?.rating !== null,
  );
  const ratingDeltas = (showRatings && game.ratingDeltas) || null;

  return (
    <div css={EOGTableStyles.table}>
      {playerAndRank.map(({ player, rank }) => {
        const user = userByID.get(player.userID)!;
        const { cardFavor, tributeFavor, prophecyFavor } =
          computePlayerFavorDetails(player, makeContextFromGame(player, game));
        const rd =
          ratingDeltas?.find((x) => x.userID === player.userID) || null;
        const delta = rd ? rd.newRating - rd.originalRating : null;
        const currentRating = rd?.newRating ?? user.stats?.rating ?? null;
        return (
          <div key={player.userID} css={EOGTableStyles.row}>
            <div css={EOGTableStyles.positionCell}>
              <FinishPosition position={rank} />
            </div>
            <div css={EOGTableStyles.picCell}>
              <img
                src={user.profilePictureURL}
                css={EOGTableStyles.profilePicture}
              />
            </div>
            <div css={EOGTableStyles.nameCell}>
              <UserHoverView user={user}>
                <div css={EOGTableStyles.name} style={{ color: user.color }}>
                  {user.name}
                </div>
              </UserHoverView>
            </div>
            <div css={EOGTableStyles.ratingCell}>
              <div css={EOGTableStyles.ratingBadge}>
                {currentRating !== null ? currentRating : '-'}
              </div>
              <div css={EOGTableStyles.ratingDelta}>
                {delta !== null ? `${delta > 0 ? '+' : ''}${delta}` : ''}
              </div>
            </div>
            <div css={EOGTableStyles.favorCell}>
              <PlayerFavorDisplay
                tributeFavor={tributeFavor}
                cardFavor={cardFavor}
                eogFavor={prophecyFavor}
                favorMode={'detailed'}
                toggleFavorMode={() => {}}
                css={EOGTableStyles.favorDisplay}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const EOGTableStyles = {
  table: css({
    display: 'grid',
    gridAutoRows: 'auto',
    rowGap: 0,
    margin: '0 auto',
    maxWidth: 520,
    width: '100%',
  }),
  row: css({
    display: 'grid',
    gridTemplateColumns: '40px 42px minmax(130px, 1fr) auto 130px',
    alignItems: 'center',
    columnGap: 8,
    padding: 6,
    [MEDIA_QUERY_MOBILE]: {
      gridTemplateColumns: '28px 32px 1fr auto auto',
      columnGap: 4,
      padding: '6px 4px',
    },
  }),
  positionCell: css({
    justifySelf: 'start',
  }),
  picCell: css({
    justifySelf: 'start',
  }),
  nameCell: css({
    justifySelf: 'start',
    overflow: 'hidden',
  }),
  ratingCell: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 6,
    width: 90,
    [MEDIA_QUERY_MOBILE]: {
      width: 75,
      gap: 4,
    },
  }),
  favorCell: css({
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  ratingDelta: css({
    fontSize: '0.8rem',
    color: UIColorPalette.ratingBadgeDeltaText,
  }),
  profilePicture: css({
    width: 36,
    height: 36,
    [MEDIA_QUERY_MOBILE]: {
      width: 30,
      height: 30,
    },
  }),
  name: css({
    fontSize: '1.31rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    textOverflow: 'clip',
    [MEDIA_QUERY_MOBILE]: {
      fontSize: '1.0rem',
    },
  }),
  ratingBadge: css({
    fontSize: '1.0rem',
    fontWeight: 700,
    color: UIColorPalette.ratingBadgeText,
    padding: '2px 8px',
    borderRadius: 6,
    background: ColorPalette.darkPurpleBG,
    border: `1px solid ${UIColorPalette.ratingBadgeBorder}`,
    boxShadow: `0 1px 2px 0 ${UIColorPalette.ratingBadgeBoxShadow}`,
    [MEDIA_QUERY_MOBILE]: {
      fontSize: '0.85rem',
      padding: '2px 6px',
    },
  }),
  favorDisplay: css({
    alignItems: 'center',
  }),
};
